/**
 * THE branch read (§5). Every reader in the ordering flow asks this, never the session's branch.
 * In `chosen` mode it never falls back to the session's branch: until one is picked, every reader
 * gets none, so the screen comes up empty rather than showing the agent's login branch.
 */
import type { OrderingContext } from "./types";

interface State {
  orderingContextStore?: OrderingContext;
  businessStore?: { businessData?: { branchId?: string; branchAddress?: { city?: string } } };
  saleDataStore?: { saleData?: { branchId?: string; saleItems?: unknown[] } };
}

export function selectOrderingContext(state: State): OrderingContext {
  return state?.orderingContextStore ?? { source: "session" };
}

export function selectOrderingBranchId(state: State): string | undefined {
  const context = selectOrderingContext(state);
  return context.source === "chosen" ? context.branch?.branchId : state?.businessStore?.businessData?.branchId;
}

/** The order's branch's city (the customiser's stock line, web v1's `branchAddress.city`); none for a chosen branch yet. */
export function selectOrderingBranchCity(state: State): string | undefined {
  return selectOrderingContext(state).source === "chosen" ? undefined : state?.businessStore?.businessData?.branchAddress?.city || undefined;
}

/**
 * The one invariant (§5): while the source is `chosen`, the working sale is empty or carries the
 * context's branch. Tests check it after every start and teardown.
 */
export function branchInvariantHolds(state: State): boolean {
  const context = selectOrderingContext(state);
  if (context.source === "session") return true;
  const sale = state?.saleDataStore?.saleData;
  const empty = !sale || !sale.saleItems?.length;
  return empty || sale.branchId === context.branch?.branchId;
}
